// Mivio Kids — Mochila Designer (knitandcarry.com yapısı, 2 Eyl 2026; figuras 3 Eyl 2026)
// blocks/mochila-designer.liquid ile birlikte çalışır. name-designer.js'den fork.
//
//  - Çanta rengi = ürünün gerçek varyant seçeneği. Swatch tıklanınca temanın
//    gizli variant-picker radio'su tıklanır (fiyat, form id, galeri temanın
//    kendi akışıyla güncellenir); radio yoksa form input[name=id] yazılır.
//  - Talla (varsa) aynı şekilde radio üzerinden sürülür.
//  - Figuras de ganchillo (numaralı katalog, 3 Eyl 2026): figürler ürün DEĞİL.
//    Adet = ürünün «Figuras de ganchillo» varyant seçeneği (figValues[n]:
//    «Sin figuras», «1 figura», «2 figuras» …) — seçim değişince ilgili radio
//    tıklanır, fiyat varyanttan gelir (+5 €/figür merdiveni). Hangi figürler
//    seçildiği sipariş satırına katalog NUMARASIYLA yazılır:
//    properties[Figuras] = «n.º 3 Flor roja · n.º 42 Oca», _config.figuras=[3,42].
//    Kartlar snippets/kc-figuras.liquid'den (data-num); kategori sekmeleri +
//    «Ver todas» (kapalıyken 12 kart); üst sınır = figValues.length − 1.
//  - Onay kutusu ve isim zorunlu; ikisi de sağlanmadan sepete eklenmez.
//  - Figür varsa ya da pack modundaysa ekleme JSON items[] ile /cart/add.js'e
//    (varyant id tasarımcı durumundan → radio/section yarışı yok); yoksa temanın
//    form yolu. BxGy indirimi mağaza tarafında otomatik.

import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';

const NAME_DISALLOWED = /[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ ]/g;
const STORAGE_PREFIX = 'mivio-mochila-designer:';
const NONE_TEXT = 'Sin figuras';
const FIG_COLLAPSED = 12; // kapalı ızgarada görünen kart sayısı

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const norm = (v) => String(v || '').toLowerCase().trim();

class MochilaDesigner extends HTMLElement {
  #abort = null;
  #observer = null;
  #patchedComponent = null;
  #originalHandleSubmit = null;
  #refs = {};
  #data = { productId: '', options: [], variants: [], hasTalla: false, colorOption: '', figOption: null, figValues: [], figUnitCents: 0 };
  #state = {
    name: '',
    talla: '',
    bagLabel: '',
    yarnLabel: '',
    yarnDisplay: '',
    yarnHex: '',
    figures: [], // [{ num, label }] — seçim sırasıyla; num = katalog numarası
    confirmed: false,
  };
  #bundle = 1;
  #submitting = false;
  #errorTimeout = null;
  #gallerySlug = '';
  #figFilter = '';
  #figExpanded = false;
  #figLimitTimeout = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#collectRefs();
    this.#readProductData();
    if (!this.#refs.cardInput) return;

    if (this.#isSecondary) this.#cloneSwatchesFromPrimary();
    this.#distributeSwatches();
    this.#restoreState();

    this.#refs.cardInput.addEventListener('input', () => {
      this.#setName(this.#refs.cardInput.value, this.#refs.cardInput);
    }, { signal });

    this.querySelectorAll('[data-nd-talla]').forEach((button) => {
      button.addEventListener('click', () => this.#selectTalla(button.dataset.ndTalla || ''), { signal });
    });
    this.querySelectorAll('[data-nd-swatch="bag"]').forEach((button) => {
      button.addEventListener('click', () => this.#selectBag(button), { signal });
    });
    this.querySelectorAll('[data-nd-swatch="yarn"]').forEach((button) => {
      button.addEventListener('click', () => this.#selectYarn(button), { signal });
    });
    this.querySelectorAll('[data-nd-swatch="fig"]').forEach((card) => {
      card.addEventListener('click', () => this.#toggleFigure(card), { signal });
    });
    this.querySelectorAll('[data-nd-fig-tab]').forEach((tab) => {
      tab.addEventListener('click', () => this.#setFigFilter(tab.dataset.ndFigTab || ''), { signal });
    });
    this.#refs.figMore?.addEventListener('click', () => {
      this.#figExpanded = !this.#figExpanded;
      this.#renderFigures();
    }, { signal });
    this.#refs.confirm?.addEventListener('change', () => {
      this.#state.confirmed = !!this.#refs.confirm.checked;
      if (this.#state.confirmed) this.#toggleError(this.#refs.confirmError, false);
      this.#persistState();
    }, { signal });
    this.querySelector('[data-nd-sizeguide]')?.addEventListener('click', (event) => {
      const id = (event.currentTarget.getAttribute('href') || '').slice(1);
      const target = id ? document.getElementById(id) : null;
      if (!target) return;
      event.preventDefault();
      const details = target.matches('details') ? target : target.querySelector('details');
      if (details) details.open = true;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, { signal });

    if (!this.#isSecondary) {
      this.#guardAddToCart();
      const sectionRoot = this.#section();
      if (sectionRoot) {
        this.#observer = new MutationObserver(() => this.#guardAddToCart());
        this.#observer.observe(sectionRoot, { childList: true, subtree: true });
      }
      this.#bindBundleCards(signal);
      this.#setBundle(this.#bundle);
      this.#section()?.addEventListener(
        ThemeEvents.variantUpdate,
        () => requestAnimationFrame(() => this.#refreshPriceDisplays()),
        { signal }
      );
    }

    this.#syncHiddenInputs();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#patchedComponent && this.#originalHandleSubmit) {
      this.#patchedComponent.handleSubmit = this.#originalHandleSubmit;
      delete this.#patchedComponent.dataset.mochilaDesignerGuarded;
    }
    this.#patchedComponent = null;
    this.#originalHandleSubmit = null;
  }

  // --- dışa açık ---------------------------------------------------------

  get value() {
    return this.#state.name.trim();
  }

  get bundle() {
    return this.#bundle;
  }

  get confirmed() {
    return !this.#refs.confirm || this.#state.confirmed;
  }

  get figureCount() {
    return this.#state.figures.length;
  }

  // Üründeki adet seçeneğinin tavanı (Sin figuras hariç); seçenek yoksa 0 → figür seçilemez.
  get maxFigures() {
    return Math.max(0, (this.#data.figValues?.length || 0) - 1);
  }

  // n figür için varyant seçeneği değeri («Sin figuras», «1 figura», «3 figuras»…)
  figValue(n) {
    return this.#data.figValues?.[n] || null;
  }

  #otherValues() {
    const values = [];
    if (this.#data.hasTalla && this.#state.talla) values.push(this.#state.talla);
    if (this.#state.bagLabel) values.push(this.#state.bagLabel);
    return values;
  }

  // Seçili talla + renk + figür adedine karşılık gelen varyant. Adet basamağı
  // yoksa (seçenek eksik) taban varyanta düşer.
  currentVariant() {
    const other = this.#otherValues();
    const figValue = this.figValue(this.figureCount);
    return (
      (figValue ? this.#findVariant([...other, figValue]) : null) ||
      this.baseVariant()
    );
  }

  // Aynı talla + renk, «Sin figuras»: fiyat rozetleri ve pack kartları bunun üzerinden.
  baseVariant() {
    const other = this.#otherValues();
    const none = this.figValue(0);
    return (
      (none ? this.#findVariant([...other, none]) : null) ||
      this.#findVariant(other) ||
      this.#findVariant([this.#state.bagLabel]) ||
      this.#data.variants?.[0] ||
      null
    );
  }

  // Figür ek ücreti = seçili varyant − taban varyant (varyant merdiveninden okunur).
  surchargeCents() {
    const v = this.currentVariant();
    const b = this.baseVariant();
    if (!v?.priceCents || !b?.priceCents) return 0;
    return Math.max(0, v.priceCents - b.priceCents);
  }

  lineProperties() {
    const name = this.#state.name.trim();
    const config = {
      nombre: name,
      mochila: this.#state.bagLabel || '',
      hilo: this.#state.yarnDisplay || this.#state.yarnLabel || '',
      hilo_hex: this.#state.yarnHex || '',
      figuras: this.#state.figures.map((f) => f.num),
    };
    if (this.#data.hasTalla) config.talla = this.#state.talla || '';
    const props = { Nombre: name };
    if (this.#data.hasTalla) props['Talla'] = this.#state.talla || '';
    props[this.#data.colorOption || 'Color de la mochila'] = this.#state.bagLabel || '';
    props['Color del hilo'] = this.#state.yarnDisplay || this.#state.yarnLabel || '';
    props['Figuras'] = this.#figuresText();
    props['_config'] = JSON.stringify(config).slice(0, 255);
    return props;
  }

  refreshPrice() {
    if (!this.#isSecondary) this.#refreshPriceDisplays();
  }

  showError() {
    this.#toggleError(this.#refs.cardError, true);
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.#refs.cardInput?.focus({ preventScroll: true });
  }

  showConfirmError() {
    this.#toggleError(this.#refs.confirmError, true);
    this.#refs.confirm?.focus({ preventScroll: true });
    this.#refs.confirm?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // --- kurulum ----------------------------------------------------------

  #collectRefs() {
    this.#refs = {
      cardInput: this.querySelector('[data-nd-card-input]'),
      cardError: this.querySelector('[data-nd-card-error]'),
      count: this.querySelector('[data-nd-count]'),
      charsHints: this.querySelectorAll('[data-nd-chars]'),
      tallaProperty: this.querySelector('[data-nd-talla-property]'),
      bagProperty: this.querySelector('[data-nd-bag-property]'),
      bagLabelOut: this.querySelector('[data-nd-bag-label]'),
      yarnProperty: this.querySelector('[data-nd-yarn-property]'),
      yarnLabelOut: this.querySelector('[data-nd-yarn-label]'),
      figsProperty: this.querySelector('[data-nd-figs-property]'),
      configProperty: this.querySelector('[data-nd-config-property]'),
      bagList: this.querySelector('[data-nd-bag-list]'),
      yarnList: this.querySelector('[data-nd-yarn-list]'),
      figList: this.querySelector('[data-nd-fig-list]'),
      figTabs: this.querySelector('[data-nd-fig-tabs]'),
      figMore: this.querySelector('[data-nd-fig-more]'),
      figsField: this.querySelector('[data-nd-figs-field]'),
      feltSummary: this.querySelector('[data-nd-felt-summary]'),
      figLimit: this.querySelector('[data-nd-fig-limit]'),
      swatchSource: this.querySelector('[data-nd-swatch-source]'),
      summary: this.querySelector('[data-nd-summary]'),
      confirm: this.querySelector('[data-nd-confirm]'),
      confirmError: this.querySelector('[data-nd-confirm-error]'),
    };
  }

  #readProductData() {
    const script = this.querySelector('script[data-nd-data]') || this.#section()?.querySelector('script[data-nd-data]');
    if (!script) return;
    try {
      this.#data = { figValues: [], ...JSON.parse(script.textContent) };
    } catch (error) {
      // veri bozuksa varyant eşleşmesi devre dışı kalır
    }
  }

  #cloneSwatchesFromPrimary() {
    const source = this.#refs.swatchSource;
    if (!source || source.children.length > 0) return;
    const primary = this.#primary();
    if (!primary) return;
    primary.querySelectorAll('[data-nd-swatch]').forEach((node) => {
      const clone = node.cloneNode(true);
      for (const el of [clone, ...clone.querySelectorAll('*')]) {
        if (el.hasAttribute('aria-pressed')) el.setAttribute('aria-pressed', 'false');
        if (el.hasAttribute('hidden')) el.removeAttribute('hidden');
        for (const attr of Array.from(el.attributes)) {
          if (attr.name.startsWith('data-shopify-editor')) el.removeAttribute(attr.name);
        }
      }
      source.appendChild(clone);
    });
  }

  // Alt bloklar (ip), Liquid'in ürettiği çanta swatch'ları ve figür kartları
  // tür bazında listelere taşınır.
  #distributeSwatches() {
    const { swatchSource, bagList, yarnList, figList } = this.#refs;
    if (!swatchSource) return;
    swatchSource.querySelectorAll('[data-nd-swatch]').forEach((node) => {
      const kind = node.dataset.ndSwatch;
      if (kind === 'bag') bagList?.appendChild(node);
      else if (kind === 'fig') figList?.appendChild(node);
      else yarnList?.appendChild(node);
    });
    // Birincil panelde çanta swatch'ları zaten listede; ikincil için klon.
    if (!this.#isSecondary && bagList && !bagList.children.length) {
      // renk seçeneği yok (örn. tek varyantlı ürün) — alan gizlenir
      bagList.closest('[data-nd-field]')?.setAttribute('hidden', '');
    }
    if (this.#isSecondary && bagList && !bagList.children.length) {
      bagList.closest('[data-nd-field]')?.setAttribute('hidden', '');
    }
    if (yarnList && !yarnList.children.length) yarnList.closest('[data-nd-field]')?.setAttribute('hidden', '');
    if (figList && !figList.children.length) this.#refs.figsField?.setAttribute('hidden', '');
    // Kategori sekmeleri: boş kategori gizlenir, sayılar yazılır
    const cards = Array.from(this.querySelectorAll('[data-nd-swatch="fig"]'));
    this.querySelectorAll('[data-nd-fig-tab]').forEach((tab) => {
      const cat = tab.dataset.ndFigTab || '';
      const n = cat ? cards.filter((c) => (c.dataset.category || '') === cat).length : cards.length;
      tab.hidden = n === 0;
      let count = tab.querySelector('.nd-figs__tab-count');
      if (!count) {
        count = document.createElement('span');
        count.className = 'nd-figs__tab-count';
        tab.append(' ', count);
      }
      count.textContent = String(n);
    });
    if (this.#refs.figTabs && cards.length && this.querySelectorAll('[data-nd-fig-tab]:not([hidden])').length < 3) {
      this.#refs.figTabs.hidden = true; // tek kategori: sekme gereksiz
    }
  }

  get #maxChars() {
    const key = this.#data.hasTalla && this.#state.talla === 'Pequeña' ? 'maxCharsSmall' : 'maxChars';
    const parsed = Number.parseInt(this.dataset[key] || this.dataset.maxChars || '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 8;
  }

  get #storageKey() {
    return STORAGE_PREFIX + (this.#data.productId || this.dataset.productId || 'product') + (this.#isSecondary ? ':2' : '');
  }

  get #isSecondary() {
    return this.hasAttribute('data-nd-secondary');
  }

  // BxGy «2.ª al 50 %»: Shopify indirimi ucuz mochilanın fiyatı üzerinden kuruşa
  // aşağı yuvarlayarak tahsis eder (49,99 € → 24,99 €). Aynı hesap burada.
  #bundleDiscountCents(v1, v2) {
    const pct = Number.parseInt(this.dataset.bundlePercent || '', 10);
    const percent = Number.isFinite(pct) && pct > 0 ? pct : 50;
    const base = Math.min(v1?.priceCents || 0, v2?.priceCents || v1?.priceCents || 0);
    return Math.floor((base * percent) / 100);
  }

  #section() {
    return this.closest('.shopify-section') || null;
  }

  #primary() {
    const el = this.#section()?.querySelector('mochila-designer:not([data-nd-secondary])');
    return el instanceof MochilaDesigner ? el : null;
  }

  #secondary() {
    const el = this.#section()?.querySelector('mochila-designer[data-nd-secondary]');
    return el instanceof MochilaDesigner ? el : null;
  }

  // --- durum ------------------------------------------------------------

  #restoreState() {
    let stored = null;
    try {
      stored = JSON.parse(window.sessionStorage.getItem(this.#storageKey) || 'null');
    } catch (error) {
      stored = null;
    }
    const tallaButtons = Array.from(this.querySelectorAll('[data-nd-talla]'));
    const tallaValues = tallaButtons.map((b) => b.dataset.ndTalla || '');
    if (stored && typeof stored === 'object') {
      this.#state.name = this.#sanitize(stored.name || '');
      if (!this.#isSecondary) this.#bundle = stored.bundle === 2 ? 2 : 1;
      if (tallaValues.includes(stored.talla)) this.#state.talla = stored.talla;
    }
    if (!this.#state.talla && tallaValues.length) {
      // birincil panelde temanın seçili varyantına uy
      const selected = this.#selectedVariantFromForm();
      const fromVariant = selected?.options?.find((o) => tallaValues.includes(o));
      this.#state.talla = fromVariant || tallaValues[0];
    }

    const yarnButtons = Array.from(this.querySelectorAll('[data-nd-swatch="yarn"]'));
    const storedYarn = stored?.yarnLabel ? yarnButtons.find((b) => b.dataset.value === stored.yarnLabel) : null;
    this.#applyYarn(storedYarn || yarnButtons[0] || null);

    const bagButtons = Array.from(this.querySelectorAll('[data-nd-swatch="bag"]'));
    let bag = stored?.bagLabel ? bagButtons.find((b) => b.dataset.value === stored.bagLabel) : null;
    if (!bag && !this.#isSecondary) {
      // URL ?variant= ya da temanın seçili varyantı
      const selected = this.#selectedVariantFromForm();
      const value = selected?.options?.find((o) => bagButtons.some((b) => b.dataset.value === o));
      bag = value ? bagButtons.find((b) => b.dataset.value === value) : null;
    }
    this.#applyBag(bag || bagButtons.find((b) => !b.hasAttribute('data-unavailable')) || bagButtons[0] || null);

    const cards = Array.from(this.querySelectorAll('[data-nd-swatch="fig"]'));
    const storedFigures = Array.isArray(stored?.figures) ? stored.figures : [];
    const seen = new Set();
    this.#state.figures = storedFigures
      .map((f) => cards.find((c) => (f && f.num != null && String(c.dataset.num) === String(f.num)) || (f && f.label && c.dataset.value === f.label)))
      .filter((c) => c && !c.disabled && !seen.has(c.dataset.num) && seen.add(c.dataset.num))
      .slice(0, this.maxFigures)
      .map((c) => this.#figureFromCard(c));
    if (typeof stored?.figFilter === 'string' && this.querySelector('[data-nd-fig-tab="' + CSS.escape(stored.figFilter) + '"]')) {
      this.#figFilter = stored.figFilter;
    }

    // Onay kutusu her sayfa yüklemesinde boş başlar (knitandcarry: her seferinde onay).
    if (this.#refs.confirm) {
      this.#state.confirmed = false;
      this.#refs.confirm.checked = false;
    }

    this.#applyTalla(this.#state.talla);
    this.#renderFigures();
    this.#renderFeltSummary();
    this.#syncVariant();

    if (this.#refs.cardInput && !this.#refs.cardInput.value) {
      this.#refs.cardInput.value = this.#state.name;
    } else if (this.#refs.cardInput) {
      this.#state.name = this.#sanitize(this.#refs.cardInput.value);
    }
    this.#renderCount();
  }

  #selectedVariantFromForm() {
    const idInput = this.#productForm()?.querySelector('input[name="id"]');
    const id = idInput ? String(idInput.value) : '';
    return this.#data.variants?.find((v) => String(v.id) === id) || null;
  }

  #persistState() {
    try {
      window.sessionStorage.setItem(this.#storageKey, JSON.stringify({
        name: this.#state.name,
        talla: this.#state.talla,
        yarnLabel: this.#state.yarnLabel,
        bagLabel: this.#state.bagLabel,
        figures: this.#state.figures.map((f) => ({ num: f.num, label: f.label })),
        figFilter: this.#figFilter,
        bundle: this.#bundle,
      }));
    } catch (error) {
      // depolama yoksa akış bozulmaz
    }
  }

  #sanitize(raw) {
    let cleaned = String(raw || '').replace(NAME_DISALLOWED, '').replace(/\s+/g, ' ').replace(/^\s+/, '');
    cleaned = cleaned.slice(0, this.#maxChars);
    if (cleaned.length > 0) cleaned = cleaned.charAt(0).toLocaleUpperCase('es') + cleaned.slice(1);
    return cleaned;
  }

  #setName(raw, sourceInput) {
    const cleaned = this.#sanitize(raw);
    if (sourceInput && sourceInput.value !== cleaned) {
      const cursor = sourceInput.selectionStart;
      sourceInput.value = cleaned;
      if (cursor !== null) {
        const position = Math.min(cursor, cleaned.length);
        sourceInput.setSelectionRange(position, position);
      }
    }
    this.#state.name = cleaned;
    const { cardInput } = this.#refs;
    if (cardInput && cardInput !== sourceInput) cardInput.value = cleaned;
    if (cleaned.trim() !== '') this.#toggleError(this.#refs.cardError, false);
    this.#renderCount();
    this.#persistState();
    this.#syncHiddenInputs();
  }

  #renderCount() {
    if (this.#refs.count) this.#refs.count.textContent = String(this.#state.name.length);
  }

  #toggleError(element, show) {
    if (!element) return;
    element.hidden = !show;
  }

  // --- talla ---------------------------------------------------------------

  #selectTalla(value) {
    this.#applyTalla(value);
    this.#syncVariant();
    this.#persistState();
    this.#syncHiddenInputs();
  }

  #applyTalla(value) {
    const buttons = Array.from(this.querySelectorAll('[data-nd-talla]'));
    if (!buttons.length) return;
    const values = buttons.map((b) => b.dataset.ndTalla || '');
    this.#state.talla = values.includes(value) ? value : values[0];
    buttons.forEach((button) => {
      button.setAttribute('aria-pressed', (button.dataset.ndTalla || '') === this.#state.talla ? 'true' : 'false');
    });
    const input = this.#refs.cardInput;
    if (input) {
      input.maxLength = this.#maxChars;
      if (input.value.length > this.#maxChars) this.#setName(input.value, input);
    }
    this.#refs.charsHints?.forEach((el) => { el.textContent = String(this.#maxChars); });
    this.#state.name = this.#sanitize(this.#state.name);
  }

  // --- swatch'lar -----------------------------------------------------------

  #selectYarn(button) {
    this.#applyYarn(button);
    this.#persistState();
    this.#syncHiddenInputs();
  }

  #applyYarn(button) {
    if (!button) return;
    this.querySelectorAll('[data-nd-swatch="yarn"]').forEach((b) => {
      b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
    });
    this.#state.yarnLabel = button.dataset.value || '';
    this.#state.yarnHex = button.dataset.hex || '';
    const components = (button.dataset.components || '').trim();
    this.#state.yarnDisplay =
      components && components !== this.#state.yarnLabel
        ? this.#state.yarnLabel + ' (' + components + ')'
        : this.#state.yarnLabel;
    if (this.#refs.yarnLabelOut) this.#refs.yarnLabelOut.textContent = this.#state.yarnLabel;
  }

  #selectBag(button) {
    this.#applyBag(button);
    this.#syncVariant();
    this.#syncGalleryToBag();
    this.#persistState();
    this.#syncHiddenInputs();
  }

  #applyBag(button) {
    if (!button) return;
    this.querySelectorAll('[data-nd-swatch="bag"]').forEach((b) => {
      b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
    });
    this.#state.bagLabel = button.dataset.value || '';
    if (this.#refs.bagLabelOut) this.#refs.bagLabelOut.textContent = this.#state.bagLabel;
  }

  // Galeri: temanın varyant→medya geçişi zaten çalışır (varyantın görseli
  // varsa). Ek olarak dosya adında renk slug'ı geçen kareye atlanır.
  #syncGalleryToBag() {
    if (this.#isSecondary) return;
    const slug = slugify(this.#state.bagLabel);
    if (!slug || slug === this.#gallerySlug) return;
    const gallery = this.#section()?.querySelector('media-gallery');
    if (!gallery) return;
    const slides = Array.from(gallery.querySelectorAll('[ref="slides[]"]'));
    const index = slides.findIndex((slide) => {
      const file = (slide.querySelector('img')?.getAttribute('src') || '').split('/').pop() || '';
      return slugify(file).includes('-' + slug + '-') || slugify(file).startsWith(slug + '-') || slugify(file).includes(slug);
    });
    if (index < 0) return;
    this.#gallerySlug = slug;
    const thumb = gallery.querySelectorAll('[ref="thumbnails[]"]')[index];
    if (thumb) {
      thumb.click();
      return;
    }
    try {
      gallery.slideshow?.select(index);
    } catch (error) {
      // galeri bileşeni farklıysa sessizce geç
    }
  }

  // --- figuras -------------------------------------------------------------

  #figureFromCard(card) {
    return { num: Number.parseInt(card.dataset.num || '', 10) || 0, label: card.dataset.value || '' };
  }

  #toggleFigure(card) {
    if (card.disabled) return;
    const fig = this.#figureFromCard(card);
    const index = this.#state.figures.findIndex((f) => f.num === fig.num);
    if (index >= 0) this.#state.figures.splice(index, 1);
    else {
      if (this.#state.figures.length >= this.maxFigures) {
        this.#toggleError(this.#refs.figLimit, true);
        clearTimeout(this.#figLimitTimeout);
        this.#figLimitTimeout = setTimeout(() => this.#toggleError(this.#refs.figLimit, false), 2500);
        return;
      }
      this.#state.figures.push(fig);
    }
    this.#toggleError(this.#refs.figLimit, false);
    this.#afterFiguresChange();
  }

  #removeFigure(num) {
    const index = this.#state.figures.findIndex((f) => f.num === num);
    if (index < 0) return;
    this.#state.figures.splice(index, 1);
    this.#afterFiguresChange();
  }

  #clearFigures() {
    if (!this.#state.figures.length) return;
    this.#state.figures = [];
    this.#afterFiguresChange();
  }

  #afterFiguresChange() {
    this.#renderFigures();
    this.#renderFeltSummary();
    this.#syncVariant();
    this.#persistState();
    this.#syncHiddenInputs();
  }

  #setFigFilter(cat) {
    this.#figFilter = cat;
    this.#figExpanded = false;
    this.#renderFigures();
    this.#persistState();
  }

  // Sipariş satırı: atölye numaraya bakar, ad doğrulama içindir.
  #figuresText() {
    if (!this.#state.figures.length) return NONE_TEXT;
    return this.#state.figures.map((f) => 'n.º ' + f.num + ' ' + f.label).join(' · ');
  }

  // Kartlar: seçim işareti + kategori filtresi + kapalı/açık ızgara.
  #renderFigures() {
    const selected = this.#state.figures.map((f) => f.num);
    const cards = Array.from(this.querySelectorAll('[data-nd-swatch="fig"]'));
    if (!cards.length) return;
    const matching = cards.filter((c) => !this.#figFilter || (c.dataset.category || '') === this.#figFilter);
    let shown = 0;
    cards.forEach((card) => {
      const num = Number.parseInt(card.dataset.num || '', 10) || 0;
      card.setAttribute('aria-pressed', selected.includes(num) ? 'true' : 'false');
      const inFilter = matching.includes(card);
      const visible = inFilter && (this.#figExpanded || shown < FIG_COLLAPSED);
      if (inFilter) shown += 1;
      card.hidden = !visible;
    });
    this.querySelectorAll('[data-nd-fig-tab]').forEach((tab) => {
      tab.setAttribute('aria-pressed', (tab.dataset.ndFigTab || '') === this.#figFilter ? 'true' : 'false');
    });
    const more = this.#refs.figMore;
    if (more) {
      const hiddenCount = matching.length - Math.min(matching.length, FIG_COLLAPSED);
      more.hidden = hiddenCount <= 0;
      more.setAttribute('aria-expanded', this.#figExpanded ? 'true' : 'false');
      more.textContent = this.#figExpanded
        ? 'Ver menos'
        : 'Ver todas las figuras (' + matching.length + ')';
    }
  }

  #renderFeltSummary() {
    const el = this.#refs.feltSummary;
    if (!el) return;
    el.textContent = '';
    if (!this.#state.figures.length) return;
    const signal = this.#abort?.signal;
    const title = document.createElement('span');
    title.className = 'nd-felt-summary__label';
    title.textContent = 'Elegidas:';
    el.append(title);
    this.#state.figures.forEach((item) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'nd-felt-chip';
      chip.setAttribute('aria-label', 'Quitar n.º ' + item.num + ' ' + item.label);
      const card = this.querySelector('[data-nd-swatch="fig"][data-num="' + item.num + '"]');
      const src = card?.querySelector('img')?.currentSrc || card?.querySelector('img')?.getAttribute('src') || '';
      if (src) {
        const thumb = document.createElement('img');
        thumb.className = 'nd-felt-chip__thumb';
        thumb.src = src;
        thumb.alt = '';
        thumb.width = 22;
        thumb.height = 22;
        chip.append(thumb);
      }
      const numBadge = document.createElement('span');
      numBadge.className = 'nd-felt-chip__num';
      numBadge.textContent = String(item.num);
      chip.append(numBadge, ' ' + item.label);
      const remove = document.createElement('span');
      remove.className = 'nd-felt-chip__x';
      remove.setAttribute('aria-hidden', 'true');
      remove.textContent = '✕';
      chip.append(remove);
      chip.addEventListener('click', () => this.#removeFigure(item.num), { signal });
      el.append(chip);
    });
    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'nd-felt-summary__clear';
    clear.textContent = 'Quitar todas';
    clear.addEventListener('click', () => this.#clearFigures(), { signal });
    el.append(clear);
    const total = document.createElement('span');
    total.className = 'nd-felt-summary__total';
    const cents = this.surchargeCents();
    total.textContent = cents > 0 ? '+' + this.#formatMoney(cents) : this.figureCount + (this.figureCount === 1 ? ' figura' : ' figuras');
    el.append(total);
  }

  // --- varyant / fiyat -----------------------------------------------------------

  #syncVariant() {
    if (this.#isSecondary) {
      this.#primary()?.refreshPrice();
      return;
    }
    const values = this.#otherValues();
    const figValue = this.figValue(this.figureCount);
    if (figValue) values.push(figValue);
    let clicked = false;
    for (const value of values) clicked = this.#clickNativeVariantRadio(value) || clicked;
    if (!clicked) {
      const variant = this.currentVariant();
      if (variant) this.#setFormVariant(variant.id);
    }
    this.#refreshPriceDisplays();
  }

  #refreshPriceDisplays() {
    if (this.#isSecondary) return;
    this.#paintBundleCards();
    // Varyant fiyatı figürleri içerir; üstü çizili fiyat ve −% rozeti taban
    // varyanttan (figür ek ücreti indirimsiz eklenir → rozet hep çantanın oranı).
    const v1 = this.currentVariant();
    const b1 = this.baseVariant() || v1;
    if (!v1?.priceCents) return;
    const s1 = this.surchargeCents();
    if (this.#bundle !== 2) {
      const price = v1.priceCents;
      const compare = b1.compareAtCents ? b1.compareAtCents + s1 : null;
      const badge = b1.discountPct > 0 ? '−' + b1.discountPct + ' %' : '';
      this.#paintPrice(this.#formatMoney(price), compare ? this.#formatMoney(compare) : null, badge);
      this.#renderSummary(this.#formatMoney(price), this.figureCount, 1);
      return;
    }
    const second = this.#secondary();
    const v2 = second ? second.currentVariant() : null;
    if (!v2?.priceCents) return;
    const b2 = second.baseVariant() || v2;
    const s2 = second.surchargeCents();
    // BxGy yüzdesi Shopify'da figürlü varyant fiyatı üzerinden uygulanır (ucuz olan).
    const price = v1.priceCents + v2.priceCents - this.#bundleDiscountCents(v1, v2);
    const compare = (b1.compareAtCents || b1.priceCents) + s1 + (b2.compareAtCents || b2.priceCents) + s2;
    const badge = compare > price ? (this.dataset.bundleBadge || '2.ª Mochila al 50 %') : '';
    this.#paintPrice(this.#formatMoney(price), compare > price ? this.#formatMoney(compare) : null, badge);
    this.#renderSummary(this.#formatMoney(price), this.figureCount + (second?.figureCount || 0), 2);
  }

  // Pack kartları taban (figürsüz) fiyatı gösterir.
  #paintBundleCards() {
    const v1 = this.baseVariant();
    if (!v1?.priceCents) return;
    const one = this.querySelector('[data-nd-bundle-price="1"]');
    if (one) one.textContent = this.#formatMoney(v1.priceCents);
    const two = this.querySelector('[data-nd-bundle-price="2"]');
    if (!two) return;
    const second = this.#secondary();
    const v2 = second ? second.baseVariant() : v1;
    if (!v2?.priceCents) return;
    const full = v1.priceCents + v2.priceCents;
    const net = full - this.#bundleDiscountCents(v1, v2);
    two.textContent = '';
    const struck = document.createElement('s');
    struck.textContent = this.#formatMoney(full);
    two.append(struck, ' ' + this.#formatMoney(net));
  }

  #renderSummary(priceText, figureCount, bags) {
    const el = this.#refs.summary;
    if (!el) return;
    if (!priceText) {
      el.textContent = '';
      return;
    }
    const parts = [bags === 2 ? '2 mochilas' : '1 mochila'];
    if (figureCount > 0) parts.push(figureCount + (figureCount === 1 ? ' figura' : ' figuras'));
    el.innerHTML = '';
    el.append(parts.join(' + ') + ' · ');
    const total = document.createElement('span');
    total.className = 'nd-summary__total';
    total.textContent = priceText;
    el.append(total, ' · Envío gratis');
  }

  #formatMoney(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'EUR');
  }

  #paintPrice(priceText, compareText, badgeText) {
    if (!priceText) return;
    document.querySelectorAll('.product-new').forEach((el) => { el.textContent = priceText; });
    document.querySelectorAll('.sticky-add-to-cart__price .price').forEach((el) => { el.textContent = priceText; });
    document.querySelectorAll('.product-compare').forEach((el) => {
      el.textContent = compareText || '';
      el.style.display = compareText ? '' : 'none';
    });
    document.querySelectorAll('.product-discount-badge').forEach((el) => {
      if (badgeText) {
        el.textContent = badgeText;
        el.style.display = '';
      } else el.style.display = 'none';
    });
  }

  // --- pack ---------------------------------------------------------------------

  #bindBundleCards(signal) {
    this.querySelectorAll('[data-nd-bundle]').forEach((button) => {
      button.addEventListener('click', () => {
        this.#setBundle(Number(button.dataset.ndBundle) === 2 ? 2 : 1);
      }, { signal });
    });
  }

  #setBundle(n) {
    const secondEl = this.#section()?.querySelector('mochila-designer[data-nd-secondary]');
    if (n === 2 && !secondEl) n = 1;
    this.#bundle = n;
    this.querySelectorAll('[data-nd-bundle]').forEach((button) => {
      button.setAttribute('aria-pressed', Number(button.dataset.ndBundle) === n ? 'true' : 'false');
    });
    const panelTitle = this.querySelector('[data-nd-panel-title]');
    if (panelTitle) panelTitle.hidden = n !== 2;
    if (secondEl) secondEl.hidden = n !== 2;
    this.#persistState();
    this.#refreshPriceDisplays();
  }

  #clickNativeVariantRadio(value) {
    if (!value) return false;
    const picker = this.#section()?.querySelector('variant-picker');
    if (!picker) return false;
    for (const radio of picker.querySelectorAll('input[type="radio"]')) {
      if (radio.value === value) {
        if (!radio.checked) radio.click();
        return true;
      }
    }
    return false;
  }

  #findVariant(optionValues) {
    const targets = (Array.isArray(optionValues) ? optionValues : [optionValues]).filter(Boolean).map(norm);
    if (!targets.length) return null;
    return (
      this.#data.variants?.find((variant) => {
        const options = (variant.options || []).map(norm);
        return targets.every((target) => options.includes(target));
      }) || null
    );
  }

  #productForm() {
    const formId = this.dataset.formId;
    return formId ? document.getElementById(formId) : null;
  }

  #setFormVariant(variantId) {
    if (this.#isSecondary) return;
    const idInput = this.#productForm()?.querySelector('input[name="id"]');
    if (idInput && variantId) {
      idInput.value = String(variantId);
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // --- Shopify -------------------------------------------------------------------

  #syncHiddenInputs() {
    const r = this.#refs;
    const props = this.lineProperties();
    if (r.tallaProperty) r.tallaProperty.value = props['Talla'] || '';
    if (r.bagProperty) r.bagProperty.value = props[this.#data.colorOption || 'Color de la mochila'] || '';
    if (r.yarnProperty) r.yarnProperty.value = props['Color del hilo'] || '';
    if (r.figsProperty) r.figsProperty.value = props['Figuras'] || NONE_TEXT;
    if (r.configProperty) r.configProperty.value = props['_config'] || '';
  }

  #guardAddToCart() {
    const section = this.#section();
    if (!section) return;
    const component = section.querySelector('product-form-component');
    if (!component) return;
    if (component.dataset.mochilaDesignerGuarded === 'true') {
      // bölüm yeniden çizildiyse buton kabı yenilenmiş olabilir
      this.#guardAddToCartAnimation(component);
      return;
    }
    if (typeof component.handleSubmit !== 'function') return;

    const original = component.handleSubmit.bind(component);
    component.handleSubmit = (event) => {
      const sectionRoot = component.closest('.shopify-section');
      const designer = sectionRoot?.querySelector('mochila-designer:not([data-nd-secondary])');
      if (!(designer instanceof MochilaDesigner)) {
        original(event);
        return;
      }
      if (designer.value === '') {
        event.preventDefault();
        designer.showError();
        return;
      }
      const second = designer.bundle === 2 ? sectionRoot?.querySelector('mochila-designer[data-nd-secondary]') : null;
      if (designer.bundle === 2 && second instanceof MochilaDesigner && second.value === '') {
        event.preventDefault();
        second.showError();
        return;
      }
      if (!designer.confirmed) {
        event.preventDefault();
        designer.showConfirmError();
        return;
      }
      const needsJson = designer.bundle === 2 || designer.figureCount > 0 || (second instanceof MochilaDesigner && second.figureCount > 0);
      if (!needsJson) {
        original(event);
        return;
      }
      event.preventDefault();
      designer.submitItems(component);
    };
    component.dataset.mochilaDesignerGuarded = 'true';
    this.#patchedComponent = component;
    this.#originalHandleSubmit = original;
    this.#guardAddToCartAnimation(component);
  }

  // Temanın add-to-cart-component'i tıklamada, form gönderilmeden önce «✓ eklendi»
  // ve sepete uçuş animasyonunu başlatır. İsim/onay eksikse animasyon da atlanır.
  #guardAddToCartAnimation(component) {
    const container = component.refs?.addToCartButtonContainer || component.querySelector('add-to-cart-component');
    if (!container || container.dataset.mochilaDesignerGuarded === 'true' || typeof container.handleClick !== 'function') return;
    const originalClick = container.handleClick.bind(container);
    container.handleClick = (event) => {
      const sectionRoot = component.closest('.shopify-section');
      const designer = sectionRoot?.querySelector('mochila-designer:not([data-nd-secondary])');
      if (designer instanceof MochilaDesigner) {
        const second = designer.bundle === 2 ? sectionRoot?.querySelector('mochila-designer[data-nd-secondary]') : null;
        const secondMissing = second instanceof MochilaDesigner && second.value === '';
        if (designer.value === '' || secondMissing || !designer.confirmed) return;
      }
      originalClick(event);
    };
    container.dataset.mochilaDesignerGuarded = 'true';
  }

  // Ana satır(lar) tek /cart/add.js isteğinde; varyant id (talla + renk + figür adedi)
  // tasarımcı durumundan çözülür, figür numaraları property'de.
  async submitItems(component) {
    if (this.#submitting) return;
    const second = this.#bundle === 2 ? this.#secondary() : null;
    const v1 = this.currentVariant();
    const v2 = second ? second.currentVariant() : null;
    if (!v1 || (second && !v2)) {
      this.#showCartError(component, 'La selección no coincide con ninguna variante; recarga la página, por favor.');
      return;
    }
    if (v1.available === false || (v2 && v2.available === false)) {
      this.#showCartError(component, 'La combinación elegida no está disponible ahora mismo.');
      return;
    }
    // Shopify sepette son ekleneni en üstte gösterir; 1.ª mochila üstte kalsın.
    const items = [];
    if (second && v2) items.push({ id: v2.id, quantity: 1, properties: second.lineProperties() });
    items.push({ id: v1.id, quantity: 1, properties: this.lineProperties() });
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component')).map((el) => el.dataset.sectionId).filter(Boolean);
    const payload = { items, sections: sectionIds.join(','), sections_url: window.location.pathname };

    this.#submitting = true;
    component?.classList?.add('is-loading');
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload),
      }).then((r) => r.json());

      if (response.status) {
        component?.dispatchEvent(new CartErrorEvent(component.id || '', response.message, response.description, response.errors));
        this.#showCartError(component, response.description || response.message || 'No se pudo añadir al carrito.');
        return;
      }
      const cart = await fetch(Theme.routes.cart_url + '.js').then((r) => r.json()).catch(() => undefined);
      component?.dispatchEvent(
        new CartAddEvent(cart ?? {}, String(v1.id), {
          source: 'product-form-component',
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: response.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.#showCartError(component, 'No se pudo añadir al carrito. Inténtalo de nuevo.');
    } finally {
      this.#submitting = false;
      component?.classList?.remove('is-loading');
    }
  }

  #showCartError(component, message) {
    const errorEl = component?.refs?.addToCartTextError;
    if (!errorEl) {
      console.error(message);
      return;
    }
    errorEl.classList.remove('hidden');
    const textNode = errorEl.childNodes[2];
    if (textNode) textNode.textContent = message;
    else errorEl.appendChild(document.createTextNode(message));
    if (component.refs.liveRegion) component.refs.liveRegion.textContent = message;
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => {
      errorEl.classList.add('hidden');
      if (component.refs.liveRegion) component.refs.liveRegion.textContent = '';
    }, 10000);
  }
}

if (!customElements.get('mochila-designer')) {
  customElements.define('mochila-designer', MochilaDesigner);
}
